import { useEffect, useMemo } from 'react';

import { useDispatch } from 'react-redux';
import { Navigate, Routes, Route, useParams, useLocation } from 'react-router';

import Dialog from '@/components/pages/Dialog/Dialog';
import Dialogs from '@/components/pages/Dialogs/Dialogs';
import Entities from '@/components/pages/Entities/Entities';
import Entity from '@/components/pages/Entity/Entity';
import { Insights } from '@/components/pages/Insights/Insights';
import Intent from '@/components/pages/Intent/Intent';
import Intents from '@/components/pages/Intents/Intents';
import { BrainLogsInfinite } from '@/components/pages/Logs/BrainLogsInfinite';
import MCPs from '@/components/pages/MCPs/MCPs';
import McpServerPage from '@/components/pages/MCPs/McpServerPage/McpServerPage';
import NotFound from '@/components/pages/NotFound/NotFound';
import { Versions } from '@/components/pages/Versions/Versions';
import WebhookConfigPage from '@/components/pages/Webhook/WebhookConfigPage';
import { WebhookSaveProvider } from '@/components/pages/Webhook/WebhookSaveAction';
import Webhooks from '@/components/pages/Webhooks/Webhooks';
import { useRtmBrain } from '@/hooks/rtm/useRtmBrain';
import { resetFilters } from '@/modules/analytics/redux/actions';
import { ImprovementsRouter } from '@/modules/improvements/ImprovementsRouter';
import { InsightFilterProvider } from '@/modules/insights/contexts/InsightFilterContext';
import { SimulationsBreadcrumbProvider } from '@/modules/simulations/SimulationsBreadcrumbContext';
import { SimulationsRouter } from '@/modules/simulations/SimulationsRouter';
import { resetBrain, setBrainId } from '@/redux/session/actions';
import { isUuid } from '@/util/util';

import { AIAgentLayout } from './AIAgentLayout';
import { AddInsight } from './components/Insights/AddInsight';
import { GuidelinesPage } from './pages/GuidelinesPage';
import { Overview } from './pages/Overview';
import { Settings } from './pages/Settings';
import { SidebarControls } from '../collections/hooks/useResizableSidebar';

interface AIAgentRouterProps {
  sidebarControls: SidebarControls<'insights'>;
}

const showSidebar = (pathname: string) => pathname.includes('/insights');

const getRightPanelContent = (
  pathname: string
): React.FC<{ toggleSidebar: (sidebar: 'insights' | null) => void }> | null => {
  if (pathname.includes('/insights')) {
    const AddInsightPanel = ({ toggleSidebar }) => (
      <InsightFilterProvider>
        <AddInsight toggleSidebar={toggleSidebar} />
      </InsightFilterProvider>
    );
    AddInsightPanel.displayName = 'AddInsightPanel';
    return AddInsightPanel;
  }

  return null;
};

export const AIAgentRouter: React.FC<AIAgentRouterProps> = ({ sidebarControls }) => {
  const dispatch = useDispatch();
  const { brainId } = useParams();
  const location = useLocation();

  const RightPanelComponent = useMemo(
    () => getRightPanelContent(location.pathname),
    [location.pathname]
  );

  const rightPanelContent = useMemo(
    () =>
      RightPanelComponent ? (
        <RightPanelComponent toggleSidebar={sidebarControls.toggleSidebar} />
      ) : null,
    [RightPanelComponent, sidebarControls.toggleSidebar]
  );

  useRtmBrain(brainId);

  useEffect(() => {
    if (brainId && isUuid(brainId)) {
      dispatch(setBrainId({ brainId }));
    }

    return () => {
      dispatch(resetBrain());
      dispatch(resetFilters());
    };
  }, [dispatch, brainId]);

  // Validate brainId is a valid UUID - check after all hooks
  if (brainId && !isUuid(brainId)) {
    return <NotFound entity="ai-agent" value={brainId} />;
  }

  return (
    <SimulationsBreadcrumbProvider>
      <WebhookSaveProvider>
        <AIAgentLayout
          showRightPanel={showSidebar(location.pathname)}
          rightPanelContent={rightPanelContent}
          sidebarControls={sidebarControls}
        >
          <Routes>
            {/* Overview */}
            <Route path="/overview" element={<Overview />} />
            {/* Backward compatibility: redirect old advanced settings route to new location */}
            <Route path="/advanced_settings" element={<Navigate to="../settings" replace />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/logs" element={<BrainLogsInfinite />} />
            <Route path="/mcps" element={<MCPs />} />
            <Route path="/mcps/:mcpId" element={<McpServerPage />} />
            <Route
              path="/insights"
              element={<Insights toggleSidebar={sidebarControls.toggleSidebar} />}
            />
            <Route path="/versions" element={<Versions />} />
            <Route path="/simulations/*" element={<SimulationsRouter />} />
            <Route path="/improvements/*" element={<ImprovementsRouter />} />

            {/* Guidelines */}
            <Route path="/guidelines" element={<GuidelinesPage />} />

            {/* Workflows */}
            <Route path="/workflows/dialogs" element={<Dialogs />} />
            <Route path="/workflows/dialogs/:dialogId" element={<Dialog />} />
            <Route path="/workflows/intents" element={<Intents />} />
            <Route path="/workflows/intents/:intentName" element={<Intent />} />
            <Route path="/workflows/entities" element={<Entities />} />
            <Route path="/workflows/entities/:entityName" element={<Entity />} />
            <Route path="/workflows/webhooks" element={<Webhooks />} />
            <Route path="/workflows/webhooks/new" element={<Webhooks />} />
            <Route path="/workflows/webhooks/:webhookId" element={<WebhookConfigPage />} />
            {/* Backward compatibility: redirect old mcps route to new location */}
            <Route path="/workflows/mcps" element={<Navigate to="../mcps" replace />} />

            {/* Fallback Routes */}
            <Route path="/" element={<Navigate to="overview" replace />} />
            <Route path="/workflows/*" element={<Navigate to="dialogs" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AIAgentLayout>
      </WebhookSaveProvider>
    </SimulationsBreadcrumbProvider>
  );
};
